import { useState, useEffect, useRef, useCallback } from "react";
import { currentBearerToken, getUserManager, login, renew, signInAgain } from "@/lib/auth";
import { API_ORIGIN } from "@/lib/apiOrigin";
import { currentAppPath, isKeycloakMode } from "@/lib/authMode";
import { tokenForConnect } from "@/lib/wsToken";
import { runtimeSession } from "@/runtime/session";

const MAX_LINES = 5000;

interface UseLogStreamReturn {
  lines: string[];
  isConnected: boolean;
  isPaused: boolean;
  togglePause: () => void;
  clearLogs: () => void;
}

export function useLogStream(): UseLogStreamReturn {
  const [lines, setLines] = useState<string[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const activeRef = useRef(false);
  const connectingRef = useRef(false);
  const pausedBufferRef = useRef<string[]>([]);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const reconnectAttemptRef = useRef(0);
  const isPausedRef = useRef(false);

  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  const connect = useCallback(async () => {
    if (!activeRef.current || connectingRef.current) return;
    connectingRef.current = true;
    const keycloak = isKeycloakMode();
    let token: string | null;
    try {
      const runtime = runtimeSession();
      token = runtime ? runtime.token() : await tokenForConnect({
        keycloak,
        getUser: async () => (await getUserManager()?.getUser()) ?? null,
        token: currentBearerToken,
        renew: () => renew(),
      });
    } catch {
      connectingRef.current = false;
      runtimeSession()?.authState("unavailable");
      return;
    }
    connectingRef.current = false;
    if (!activeRef.current) return;
    if (!token) {
      await (keycloak ? signInAgain(currentAppPath()) : login());
      return;
    }
    const apiOrigin = new URL(API_ORIGIN);
    apiOrigin.protocol = apiOrigin.protocol === "https:" ? "wss:" : "ws:";
    apiOrigin.pathname = "/api/logs/stream";
    const ws = new WebSocket(apiOrigin, ["bearer", token]);

    ws.onopen = () => {
      reconnectAttemptRef.current = 0;
      setIsConnected(true);
      if (pausedBufferRef.current.length > 0) {
        setLines((prev) => {
          const next = [...prev, ...pausedBufferRef.current];
          pausedBufferRef.current = [];
          return next.slice(-MAX_LINES);
        });
      }
    };

    ws.onclose = (event) => {
      setIsConnected(false);
      if (!activeRef.current) return;
      if (runtimeSession() && event.code === 1008) {
        runtimeSession()?.authState("forbidden");
        return;
      }
      reconnectAttemptRef.current += 1;
      const delay = Math.min(30_000, 3_000 * 2 ** Math.min(reconnectAttemptRef.current - 1, 4));
      reconnectTimerRef.current = setTimeout(() => void connect(), delay);
    };

    ws.onerror = () => {
      ws.close();
    };

    ws.onmessage = (event) => {
      const text = event.data as string;
      if (isPausedRef.current) {
        pausedBufferRef.current = [...pausedBufferRef.current, text].slice(-MAX_LINES);
        return;
      }
      setLines((prev) => [...prev, text].slice(-MAX_LINES));
    };

    wsRef.current = ws;
  }, []);

  useEffect(() => {
    activeRef.current = true;
    void connect();
    return () => {
      activeRef.current = false;
      clearTimeout(reconnectTimerRef.current);
      connectingRef.current = false;
      wsRef.current?.close();
    };
  }, [connect]);

  const togglePause = useCallback(() => {
    setIsPaused((prev) => !prev);
  }, []);

  const clearLogs = useCallback(() => {
    setLines([]);
    pausedBufferRef.current = [];
  }, []);

  return { lines, isConnected, isPaused, togglePause, clearLogs };
}
